import React, { useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../../authConfig";
import {
  showSuccessMessage,
  showErrorMessage,
} from "../../shared/Services/toasterService";
import { useNavigate } from "react-router-dom";
import apiManager from "../../api/apiManager";
import API_ENDPOINTS from "../../api/apiEndpoints";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useLocalization } from "../../context/LocalizationContext";
import { UserRole } from "../../shared/Enums/UserRole";
import "../../assets/scss/styles.scss";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LOGO_URL = process.env.REACT_APP_LOGIN_LOGO_URL || "/assets/images/logo/AutoMenuLogo.png";
const LoginPage = () => {
  const { t: localization } = useLocalization();
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const [hasCheckedUser, setHasCheckedUser] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMsLoading, setIsMsLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isEmailLoading, setIsEmailLoading] = useState(false);

  /** --------------------------
   * Helper: Save backend tokens
   * -------------------------- */
  const saveBackendTokens = ({
    accessToken,
    refreshToken,
    accessTokenExpiresAt,
    role,
    tenantUserId,
  }) => {
    localStorage.setItem("accessToken", accessToken || "");
    localStorage.setItem("refreshToken", refreshToken || "");
    localStorage.setItem("accessTokenExpiry", accessTokenExpiresAt || "");
    if (role !== null && role !== undefined) {
      localStorage.setItem("userRole", String(role));
    }
  };

  /** --------------------------
   * Helper: Acquire Graph token
   * -------------------------- */
  const getGraphToken = async () => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return tokenResponse.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const tokenResponse = await instance.acquireTokenPopup(loginRequest);
        return tokenResponse.accessToken;
      }
      throw error;
    }
  };

  /** --------------------------
   * Helper: Fetch Graph profile
   * -------------------------- */
  const fetchProfile = async () => {
    const token = await getGraphToken();
    const res = await fetch("https://graph.microsoft.com/v1.0/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.json();
  };

  /** --------------------------
   * Create a new user in backend
   * -------------------------- */
  const createNewUser = async (userData) => {
    const response = await apiManager.post(
      API_ENDPOINTS.DEALER_CREATE_USER,
      userData,
    );
    if (response.status === 200) {
      saveBackendTokens(response.data);
      showSuccessMessage("User created and logged in successfully!");
      return response.data;
    }
    throw new Error("User creation failed with status: " + response.status);
  };

  /** --------------------------
   * Handle user creation & redirect
   * -------------------------- */
  const handleUserCreationAndRedirect = async () => {
    const profile = await fetchProfile();
    if (!profile?.mail)
      return showErrorMessage("Unable to fetch profile or missing email.");

    const newUser = {
      userName: profile.mail,
      tenantId: localStorage.getItem("tenantId"),
      userRoleCode: UserRole.SERVICE_DESK_USER,
      displayName: profile.displayName || profile.givenName || "User",
    };
    await createNewUser(newUser);
    localStorage.setItem("userRole", newUser.userRoleCode);
    if (newUser.userRoleCode === UserRole.SERVICE_DESK_USER) {
      navigate("/service-desk-dashboard");
    } else {
      redirectToDashboard(newUser.userRoleCode);
    }
  };

  const checkUserAndNavigate = async (userEmail) => {
    setIsLoading(true);
    try {
      const response = await apiManager.post(API_ENDPOINTS.DEALER_GET_USER, {
        email: userEmail,
      });
      if (response.status === 200 && response.data) {
        saveBackendTokens(response.data);
        localStorage.setItem("tenantUserId", response.data.id);
        if (response.data.role === UserRole.SERVICE_DESK_USER) {
          if (response.data.assignedDealer) {
            localStorage.setItem(
              "selectedDealershipId",
              String(response.data.assignedDealer),
            );
          }
          navigate("/service-desk-dashboard");
        } else if (response.data.role) {
          redirectToDashboard(response.data.role);
        } else {
          await handleUserCreationAndRedirect();
        }
      } else {
        await handleUserCreationAndRedirect();
      }
    } catch (err) {
      showErrorMessage(err?.message || err);
      await handleUserCreationAndRedirect();
    } finally {
      setIsLoading(false);
    }
  };

  /** --------------------------
   * Redirect based on role
   * -------------------------- */
  const redirectToDashboard = (role) => {
    switch (role) {
      case UserRole.DEALER_ADMIN:
        navigate("/admin-dashboard");
        break;
      case UserRole.SERVICE_DESK_USER:
        navigate("/service-desk-dashboard");
        break;
      default:
        showErrorMessage("Unknown role: " + role);
        navigate("/unknown-role");
    }
  };

  const handleMsLogin = async () => {
    // Prevent multiple clicks while interaction is in progress
    if (inProgress !== InteractionStatus.None || isMsLoading) {
      showErrorMessage(
        "Interaction already in progress. Please wait for the current request to complete before trying again."
      );
      return;
    }

    setIsMsLoading(true);
    try {
      const response = await instance.loginPopup(loginRequest);
      if (!response?.account)
        return showErrorMessage("No account info after login");

      instance.setActiveAccount(response.account);
      localStorage.setItem("tenantId", response.account.idTokenClaims?.tid);
      setHasCheckedUser(true);
      checkUserAndNavigate(response.account.username);
    } catch (err) {
      // Handle interaction in progress error
      if (err?.errorCode === "interaction_in_progress") {
        showErrorMessage(
          "Interaction already in progress. Please wait for the current request to complete before trying again."
        );
      } else if (err?.errorCode === "popup_window_blocked") {
        showErrorMessage(
          "The login popup was blocked. Please allow popups and try again."
        );
      } else if (err?.errorCode === "user_cancelled") {
        showErrorMessage(
          "Sign-in cancelled. Please try again if you want to continue."
        );
        return;
      } else {
        showErrorMessage(err?.message || err);
      }
    } finally {
      setIsMsLoading(false);
    }
  };

  const validateEmailForm = () => {
    let valid = true;
    if (!email.trim()) {
      setEmailError("Email is required.");
      valid = false;
    } else if (!EMAIL_REGEX.test(email.trim())) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    } else {
      setEmailError("");
    }
    if (!password) {
      setPasswordError("Password is required.");
      valid = false;
    } else {
      setPasswordError("");
    }
    return valid;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!validateEmailForm()) return;
    setIsEmailLoading(true);
    try {
      const response = await apiManager.post(
        API_ENDPOINTS.EXTERNAL_USER_LOGIN,
        {
          email: email.trim(),
          password,
        },
      );

      if (response.status === 200 && response.data) {
        saveBackendTokens(response.data);
        localStorage.setItem("tenantUserId", response.data.id || "");
        if (response.data.assignedDealer) {
          localStorage.setItem(
            "selectedDealershipId",
            String(response.data.assignedDealer),
          );
        }
        const role = response.data.role;
        if (role) {
          localStorage.setItem("userRole", role);
          redirectToDashboard(role);
        } else {
          showErrorMessage(
            "Login successful but no role assigned. Contact your administrator.",
          );
        }
      }
    } catch (err) {
      showErrorMessage(err || "Login failed. Please try again.");
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleForgotPassword = () => {
    const params = email.trim()
      ? `?email=${encodeURIComponent(email.trim())}`
      : "";
    navigate(`/forgot-password${params}`);
  };

  useEffect(() => {
    if (isAuthenticated && accounts.length && !hasCheckedUser) {
      setHasCheckedUser(true);
      instance.setActiveAccount(accounts[0]);
      checkUserAndNavigate(accounts[0].username);
    }
  }, [isAuthenticated, accounts, hasCheckedUser]);

  if (isLoading) {
    return (
      <div className="loginwrapper">
        <div className="login-card">
          <div className="login-logo">
             <img src={LOGO_URL} alt="logo" />
          </div>
          <p className="login-subtitle">
            {localization("loading_please_wait")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="loginwrapper">
      <div className="login-card">
        <div className="login-logo">
          <img src={LOGO_URL} alt="logo" />
        </div>

        <p className="login-subtitle">
          {localization("sign_in_to_your_service_portal")}
        </p>

        <button
          className="btn-ms-login"
          onClick={handleMsLogin}
          disabled={isEmailLoading || isMsLoading}
          type="button"
        >
          <img
            src="/assets/images/icons/four-square-white-icon.svg"
            alt="Microsoft logo"
          />
          {localization("login_button")}
        </button>

        <div className="login-or-divider">or use a local account</div>


        <form className="login-form" onSubmit={handleEmailLogin} noValidate>
          <div className="form-group">
            <label htmlFor="login-email">Email</label>
            <div className="input-icon-wrapper">
              <i className="fas fa-envelope input-icon" />
              <input
                id="login-email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError("");
                }}
                autoComplete="email"
              />
            </div>
            {emailError && <p className="field-error">{emailError}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <div className="input-icon-wrapper">
              <i className="fas fa-lock input-icon" />
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError("");
                }}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="eye-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i
                  className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}
                />
              </button>
            </div>
            {passwordError && <p className="field-error">{passwordError}</p>}
          </div>

          <div className="login-remember-row">
            <button
              type="button"
              className="forgot-password-link"
              onClick={handleForgotPassword}
            >
              Forgot password?
            </button>
          </div>

          <button type="submit" className="btn-login" disabled={isEmailLoading}>
            {isEmailLoading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
